import React, { useMemo } from 'react';
import { getClaimCodes, isHighlightedEntitlement } from '../../../util/subscribedAccessUtils';
import { Box, Icon, Typography } from '@mui/material';
import { Cancel, Check, Warning } from '@mui/icons-material';
import { HtmlTooltip } from '../../General/Reusable/HtmlTooltip';
import { ClaimRowData, EntitlementRowData } from './entitlements-ui-types';
import { EntitlementCode } from '../../../types/subscribed-access';
import { TooltipCellConfig } from '../../General/Table/table-ui-types';
import { renderDateString } from '../../../util/utils';

function uniqueFilter(value: unknown, index: number, self: unknown[]) {
  return self.indexOf(value) === index;
}

const groupBy = <T, K extends keyof any>(arr: T[], key: (i: T) => K) =>
  arr.reduce(
    (groups, item) => {
      (groups[key(item)] ||= []).push(item);
      return groups;
    },
    {} as Record<K, T[]>
  );

const EntitlementStatusIcon = (value: EntitlementRowData) => {
  const hasInactiveClaims = value.claims?.some((e: ClaimRowData) =>
    isHighlightedEntitlement({ ...e, brandCode: e.brand })
  );
  const hasNoActiveClaims = value.claims?.every((e: ClaimRowData) =>
    isHighlightedEntitlement({ ...e, brandCode: e.brand })
  );

  if (hasInactiveClaims) {
    return (
      <div style={{ color: 'warning' }}>
        <HtmlTooltip
          placement="bottom-start"
          title={
            hasNoActiveClaims ? (
              <Typography sx={{ color: 'inherit' }}>All claims are inactive</Typography>
            ) : (
              <>
                Has claims that are <em>not</em> applicable at this time
              </>
            )
          }
          style={{ color: hasNoActiveClaims ? 'error' : 'warning' }}>
          <Icon color={hasNoActiveClaims ? 'error' : 'warning'}>
            {(hasNoActiveClaims && <Cancel />) || <Warning />}
          </Icon>
        </HtmlTooltip>
      </div>
    );
  } else {
    return (
      <HtmlTooltip
        placement="bottom-start"
        title={<Typography sx={{ color: 'inherit' }}>All claims are applicable</Typography>}>
        <Icon>
          <Check></Check>
        </Icon>
      </HtmlTooltip>
    );
  }
};

const renderClaimCodes = (value: EntitlementRowData) => {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        flexGrow: '1',
        alignItems: 'center',
      }}>
      {EntitlementStatusIcon(value)}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexDirection: 'column',
          flexGrow: '1',
          justifyContent: 'center',
        }}>
        {Object.values(groupBy(getClaimCodes(value), (c) => c.brandCode)).map(
          (codes: EntitlementCode[], index) => (
            <Box
              key={index}
              sx={{
                justifyContent: 'space-between',
                display: 'flex',
                alignItems: 'center',
              }}>
              <div style={{ padding: '0 1em', alignSelf: 'start' }}>{codes[0].brandCode}</div>
              <div style={{ padding: '0 1em' }}>
                {useMemo(
                  () =>
                    codes
                      .map((c) => c.code)
                      .filter(uniqueFilter)
                      .map((c, index) => {
                        return (
                          <div key={index} style={{ textAlign: 'end' }}>
                            {c}
                          </div>
                        );
                      }),
                  [codes]
                )}
              </div>
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};
export const entitlementRowCellConfig: TooltipCellConfig<EntitlementRowData>[] = [
  {
    title: 'Claim codes',
    propertyKey: 'claims',
    render: renderClaimCodes,
    width: 2.4,
    showTooltip: false,
  },
  { title: 'Process', propertyKey: 'process', width: 1.0, showTooltip: false },
  { title: 'System', propertyKey: 'system', width: 1.0 },
  { title: 'Source ID', propertyKey: 'sourceId', width: 1.4 },
  {
    title: 'Earliest valid from (if applicable)',
    propertyKey: 'earliestValidFrom',
    width: 1.6,
    render: renderDateString,
  },
  {
    title: 'Latest valid to (if applicable)',
    propertyKey: 'latestValidTo',
    width: 1.6,
    render: renderDateString,
  },
  { title: 'Imported by', propertyKey: 'importedBy', width: 1.0 },
];
